import React, { useState, useEffect } from "react";
import {
  Card,
  Select,
  Input,
  DatePicker,
  Row,
  Col,
  Button,
  Modal,
  DatePickerProps,
  Flex,
  Spin
} from "antd";
import { ReactComponent as CalenderIcon } from "../../Icons/vuesax/calendar.svg";
import { ReactComponent as DocumentIcon } from "../../Icons/vuesax/document-text-1.svg";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { getReport, getReportParameters } from "api/services/ReportService";
import {
  TReportFieldKeys,
  TReportFields,
  TReportFilters,
  TReportParameters
} from "api/services/models/types";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import dayjs from "dayjs";
import { showErrorToast } from "helpers/utils";
import { WebDPrivilege } from "api/services/models/WebDUser";
import ManageReportAccess from "components/reports/ManageReportAccess";
import { PickerTimeProps } from "antd/es/time-picker";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface ReportFilterProps {
  selectedReport: any;
}

interface InputFieldProps<T> {
  label: string;
  value: string[];
  multiple?: string;
  style?: React.CSSProperties;
  onChange: (value: string[]) => void;
  options?: T[];
}

interface InputNumberFieldProps {
  label: string;
  value?: number;
  style?: React.CSSProperties;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface InputStringFieldProps {
  label: string;
  value: string;
  style?: React.CSSProperties;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface DateRangePickerProps {
  label: string;
  style?: React.CSSProperties;
}

interface DatePickerFieldProps {
  label: string;
  picker?: any;
  style?: React.CSSProperties;
  value: string | null;
  onChange: (date: string | null) => void;
}

const InputField = <T extends { id: number | string; name: string }>({
  label,
  value,
  onChange,
  options,
  style = {},
  multiple = "N"
}: InputFieldProps<T>) => {
  const isMultiple = multiple === "Y";

  const normalizeValue = (value: string | string[] | number | number[]): string[] => {
    if (Array.isArray(value)) {
      return value.map(v => String(v));
    }
    return [String(value)];
  };

  const handleChange = (selectedValues: string | string[] | number | number[]) => {
    const newValue = normalizeValue(selectedValues);
    onChange(newValue);
  };

  return (
    <div style={{ flex: 1, ...style }}>
      <label
        htmlFor={label}
        style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
        {label}
      </label>
      <Select
        id={label}
        mode={isMultiple ? "multiple" : undefined}
        variant="borderless"
        value={normalizeValue(value)}
        onChange={handleChange}
        style={{ width: "100%", border: "1px solid #7777774D", borderRadius: "12px" }}
        placeholder={`Please select ${label}`}
        maxTagCount="responsive"
        showSearch
        filterOption={(input, option) => {
          if (option) {
            const optionName = option.props.children as string;
            return optionName.toLowerCase().includes(input.toLowerCase());
          }
          return false;
        }}>
        {options?.map(option => (
          <Select.Option key={option.id} value={String(option.id)}>
            {option.name}
          </Select.Option>
        ))}
      </Select>
    </div>
  );
};

const InputNumberField: React.FC<InputNumberFieldProps> = ({
  label,
  value,
  onChange,
  style = {}
}) => (
  <div style={{ flex: 1, ...style }}>
    <label
      htmlFor={label}
      style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
      {label}
    </label>
    <Input
      type="number"
      id={label}
      value={value}
      onChange={onChange}
      style={{ width: "100%", border: "1px solid #7777774D", borderRadius: "12px" }}
    />
  </div>
);

const InputStringField: React.FC<InputStringFieldProps> = ({
  label,
  value,
  onChange,
  style = {}
}) => (
  <div style={{ flex: 1, ...style }}>
    <label
      htmlFor={label}
      style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
      {label}
    </label>
    <Input
      id={label}
      value={value}
      onChange={onChange}
      style={{ width: "100%", border: "1px solid #7777774D", borderRadius: "12px" }}
    />
  </div>
);

const DateRangePicker: React.FC<DateRangePickerProps> = ({ label, style = {} }) => (
  <div style={{ flex: 1, ...style }}>
    <label
      htmlFor={label}
      style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
      {label}
    </label>
    <RangePicker
      style={{ width: "100%", borderRadius: "12px", borderColor: "#7777774D" }}
      suffixIcon={<CalenderIcon style={{ height: "16px", width: "16px", color: "#000000" }} />}
    />
  </div>
);

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  picker,
  style = {},
  value,
  onChange
}) => {
  const handleDateChange = (date: any) => {
    if (date) {
      const adjustedDate = new Date(date);
      adjustedDate.setHours(0, 0, 0, 0);
      const formattedDate = dayjs(adjustedDate).format("YYYY-MM-DD");
      onChange(formattedDate);
    } else {
      onChange(null);
    }
  };
  return (
    <div style={{ flex: 1, ...style }}>
      <label
        htmlFor={label}
        style={{ fontSize: "12px", display: "block", marginBottom: "4px" }}>
        {label}
      </label>
      <DatePicker
        style={{ width: "100%", borderRadius: "12px", borderColor: "#7777774D" }}
        onChange={handleDateChange}
        picker={picker}
        value={value ? dayjs(value) : null}
        suffixIcon={
          <CalenderIcon style={{ height: "16px", width: "16px", color: "#000000" }} />
        }
      />
    </div>
  );
};

const ReportFilter: React.FC<ReportFilterProps> = ({ selectedReport }) => {
  const [selectedReportTypeIds, setSelectedReportTypeIds] = useState<string[]>([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState<string[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedProviderIds, setSelectedProviderIds] = useState<string[]>([]);
  const [selectedSpecialityIds, setSelectedSpecialityIds] = useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedAuditEventIds, setSelectedAuditEventIds] = useState<string[]>([]);
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [dateOfDictation, setDateOfDictation] = useState<string | null>(null);
  const [dateOfDictationFrom, setDateOfDictationFrom] = useState<string | null>(null);
  const [dateOfDictationTo, setDateOfDictationTo] = useState<string | null>(null);
  const [dateOfService, setDateOfService] = useState<string | null>(null);
  const [dateOfServiceFrom, setDateOfServiceFrom] = useState<string | null>(null);
  const [dateOfServiceTo, setDateOfServiceTo] = useState<string | null>(null);
  const [dateOfUploadFrom, setDateOfUploadFrom] = useState<string | null>(null);
  const [dateOfUploadTo, setDateOfUploadTo] = useState<string | null>(null);
  const [dateOfFaxFrom, setDateOfFaxFrom] = useState<string | null>(null);
  const [dateOfFaxTo, setDateOfFaxTo] = useState<string | null>(null);
  const [jobMonth, setJobMonth] = useState<string[]>([]);
  const [jobYear, setJobYear] = useState<string[]>([]);
  const { locations, regions, providers, documentTypes, specialities, users } = usePicklists();

  const [numberOfDays, setNumberOfDays] = useState<number>();
  const [patientName, setPatientName] = useState<string>("");
  const [mrn, setMrn] = useState<string>("");
  const [clientNotes, setClientNotes] = useState<string>("");
  const [reportParameters, setReportParameters] = useState<TReportParameters[]>([]);
  const { accountId, webDUser } = useTDAuthentication();
  const possibleControlIds: TReportFieldKeys[] = [
    "REGION",
    "LOCATIONID",
    "PROVIDER_ID",
    "REPORT_TYPE",
    "SPECIALTY_ID",
    "USERID",
    "AUDITEVENT",
    "NUMBER_OF_DAYS",
    "JOB_MONTH",
    "JOB_YEAR",
    "DATE_OF_DICTATION",
    "DATE_OF_DICTATION_FROM",
    "DATE_OF_DICTATION_TO",
    "DATE_OF_SERVICE",
    "DATE_OF_SERVICE_FROM",
    "DATE_OF_SERVICE_TO",
    "DATE_OF_UPLOAD_FROM",
    "DATE_OF_UPLOAD_TO",
    "STATUS",
    "DATE_OF_FAX_FROM",
    "DATE_OF_FAX_TO",
    "SHOW_PATIENT_NAME",
    "PATIENT_NAME",
    "MRN",
    "CLIENT_NOTES"
  ];
  const dateParameters: TReportFieldKeys[] = [
    "JOB_MONTH",
    "JOB_YEAR",
    "DATE_OF_DICTATION_FROM",
    "DATE_OF_DICTATION_TO",
    "DATE_OF_DICTATION",
    "DATE_OF_SERVICE_FROM",
    "DATE_OF_SERVICE_TO",
    "DATE_OF_SERVICE",
    "DATE_OF_UPLOAD_FROM",
    "DATE_OF_UPLOAD_TO",
    "DATE_OF_FAX_FROM",
    "DATE_OF_FAX_TO"
  ];
  const inputParameters: TReportFieldKeys[] = [
    "REGION",
    "LOCATIONID",
    "PROVIDER_ID",
    "REPORT_TYPE",
    "SPECIALTY_ID",
    "USERID",
    "AUDITEVENT",
    "NUMBER_OF_DAYS",
    "PATIENT_NAME",
    "MRN",
    "CLIENT_NOTES",
    "STATUS"
  ];
  const [visibleControls, setVisibleControls] =
    useState<TReportFieldKeys[]>(possibleControlIds);
  const shouldShowDateCard = dateParameters.some(param => visibleControls.includes(param));
  const shouldShowInputCard = inputParameters.some(param => visibleControls.includes(param));
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isParametersLoading, setIsParametersLoading] = useState<boolean>(false);

  const showModal = () => {
    if (webDUser?.hasPrivilege(WebDPrivilege.ManageReportAccess)) {
      setIsModalVisible(true);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const resetStates = () => {
    setSelectedReportTypeIds([]);
    setSelectedRegionIds([]);
    setSelectedLocationIds([]);
    setSelectedProviderIds([]);
    setSelectedSpecialityIds([]);
    setSelectedUserIds([]);
    setSelectedAuditEventIds([]);
    setSelectedStatusIds([]);
    setDateOfDictation(null);
    setDateOfDictationFrom(null);
    setDateOfDictationTo(null);
    setDateOfService(null);
    setDateOfServiceFrom(null);
    setDateOfServiceTo(null);
    setDateOfUploadFrom(null);
    setDateOfUploadTo(null);
    setDateOfFaxFrom(null);
    setDateOfFaxTo(null);
    setJobMonth([]);
    setJobYear([]);
    setNumberOfDays(undefined);
    setPatientName("");
    setMrn("");
    setClientNotes("");
    setVisibleControls(possibleControlIds);
  };

  function isMultiSelectEnabled(parameterName: string): string | undefined {
    const parameter = reportParameters?.find(param => param.parameterName === parameterName);
    return parameter?.multiSelect;
  }

  useEffect(() => {
    resetStates();
  }, [accountId]);

  useEffect(() => {
    if (selectedReport) {
      setIsParametersLoading(true);
      resetStates();
      const fetchVisibilitySettings = async () => {
        try {
          const parameters = await getReportParameters(
            accountId || "",
            webDUser?.user.userId || "",
            selectedReport.key
          );
          setReportParameters(parameters);
          setVisibleControls(
            parameters?.map(control => control.parameterName as TReportFieldKeys) || []
          );
          setIsParametersLoading(false);
        } catch (error) {
          console.error("Error fetching control visibility:", error);
          setIsParametersLoading(false);
        }
      };

      fetchVisibilitySettings();
    }
  }, [selectedReport]);

  function getStatusOptions(): { id: string; name: string }[] {
    if (selectedReport?.key?.toLowerCase().includes("WCIORTHOSURGERY Missing Jobs")) {
      return [
        { id: "Dictated", name: "Dictated" },
        { id: "Not dictated", name: "Not dictated" }
      ];
    } else {
      return [
        { id: "Missing", name: "Missing" },
        { id: "Completed", name: "Completed" },
        { id: "Incomplete", name: "Incomplete" }
      ];
    }
  }

  const months = [
    { id: 1, name: "January" },
    { id: 2, name: "February" },
    { id: 3, name: "March" },
    { id: 4, name: "April" },
    { id: 5, name: "May" },
    { id: 6, name: "June" },
    { id: 7, name: "July" },
    { id: 8, name: "August" },
    { id: 9, name: "September" },
    { id: 10, name: "October" },
    { id: 11, name: "November" },
    { id: 12, name: "December" }
  ];

  const handleGenerateReport = async () => {
    if (!selectedReport) {
      showErrorToast(`Please select a report from left pane before continuing`);
      return;
    }

    const fieldMappings: TReportFields = {};

    if (visibleControls.includes("DATE_OF_DICTATION")) {
      fieldMappings.DATE_OF_DICTATION = dateOfDictation;
    }
    if (visibleControls.includes("DATE_OF_DICTATION_FROM")) {
      fieldMappings.DATE_OF_DICTATION_FROM = dateOfDictationFrom;
    }
    if (visibleControls.includes("DATE_OF_DICTATION_TO")) {
      fieldMappings.DATE_OF_DICTATION_TO = dateOfDictationTo;
    }
    if (visibleControls.includes("DATE_OF_SERVICE")) {
      fieldMappings.DATE_OF_SERVICE = dateOfService;
    }
    if (visibleControls.includes("DATE_OF_SERVICE_FROM")) {
      fieldMappings.DATE_OF_SERVICE_FROM = dateOfServiceFrom;
    }
    if (visibleControls.includes("DATE_OF_SERVICE_TO")) {
      fieldMappings.DATE_OF_SERVICE_TO = dateOfServiceTo;
    }
    if (visibleControls.includes("REGION")) {
      fieldMappings.REGION = selectedRegionIds;
    }
    if (visibleControls.includes("LOCATIONID")) {
      fieldMappings.LOCATIONID = selectedLocationIds;
    }
    if (visibleControls.includes("PROVIDER_ID")) {
      fieldMappings.PROVIDER_ID = selectedProviderIds;
    }
    if (visibleControls.includes("REPORT_TYPE")) {
      fieldMappings.REPORT_TYPE = selectedReportTypeIds;
    }
    if (visibleControls.includes("SPECIALTY_ID")) {
      fieldMappings.SPECIALTY_ID = selectedSpecialityIds;
    }
    if (visibleControls.includes("USERID")) {
      fieldMappings.USERID = selectedUserIds;
    }
    if (visibleControls.includes("AUDITEVENT")) {
      fieldMappings.AUDITEVENT = selectedAuditEventIds;
    }
    if (visibleControls.includes("STATUS")) {
      fieldMappings.STATUS = selectedStatusIds;
    }
    if (visibleControls.includes("NUMBER_OF_DAYS")) {
      fieldMappings.NUMBER_OF_DAYS = numberOfDays;
    }
    if (visibleControls.includes("SHOW_PATIENT_NAME")) {
      fieldMappings.SHOW_PATIENT_NAME = "true";
    }
    // Uncomment and add checks for JOB_MONTH and JOB_YEAR as needed
    // if (visibleControls.includes("JOB_MONTH")) { ... }
    // if (visibleControls.includes("JOB_YEAR")) { ... }

    if (visibleControls.includes("DATE_OF_UPLOAD_FROM")) {
      fieldMappings.DATE_OF_UPLOAD_FROM = dateOfUploadFrom;
    }

    if (visibleControls.includes("DATE_OF_UPLOAD_TO")) {
      fieldMappings.DATE_OF_UPLOAD_TO = dateOfUploadTo;
    }

    if (visibleControls.includes("DATE_OF_FAX_FROM")) {
      fieldMappings.DATE_OF_FAX_FROM = dateOfFaxFrom;
    }

    if (visibleControls.includes("JOB_MONTH")) {
      fieldMappings.JOB_MONTH = [jobMonth?.toString() ?? ""];
    }

    if (visibleControls.includes("JOB_YEAR")) {
      fieldMappings.JOB_YEAR = [jobYear?.toString() ?? ""];
    }

    if (visibleControls.includes("DATE_OF_FAX_FROM")) {
      fieldMappings.DATE_OF_FAX_FROM = dateOfFaxFrom;
    }

    if (visibleControls.includes("DATE_OF_FAX_TO")) {
      fieldMappings.DATE_OF_FAX_TO = dateOfFaxTo;
    }

    if (visibleControls.includes("PATIENT_NAME")) {
      fieldMappings.PATIENT_NAME = patientName;
    }

    if (visibleControls.includes("MRN")) {
      fieldMappings.MRN = mrn;
    }

    if (visibleControls.includes("CLIENT_NOTES")) {
      fieldMappings.CLIENT_NOTES = clientNotes;
    }

    const ignoredControls = ["ACCOUNTID", "GENERATED_BY"];
    for (const control of visibleControls) {
      if (ignoredControls.includes(control)) {
        continue;
      }
      if (Array.isArray(fieldMappings[control])) {
        if (fieldMappings[control].length === 0) {
          showErrorToast(`Please fill in the ${control.replace(/_/g, " ").toLowerCase()}.`);
          return;
        }
      } else if (!fieldMappings[control]) {
        showErrorToast(`Please fill in the ${control.replace(/_/g, " ").toLowerCase()}.`);
        return;
      }
    }

    console.log("Generating report with filters:", {
      selectedRegionIds,
      selectedLocationIds,
      selectedProviderIds,
      selectedReportTypeIds,
      selectedSpecialityIds,
      selectedUserIds,
      selectedStatusIds,
      selectedAuditEventIds,
      numberOfDays,
      patientName,
      mrn,
      clientNotes,
      dateOfDictationFrom,
      dateOfDictationTo,
      dateOfServiceFrom,
      dateOfServiceTo
    });

    const reportFilter: TReportFilters = {
      dateOfDictation: fieldMappings.DATE_OF_DICTATION || null,
      dateOfDictationFrom: fieldMappings.DATE_OF_DICTATION_FROM || null,
      dateOfDictationTo: fieldMappings.DATE_OF_DICTATION_TO || null,
      dateOfService: fieldMappings.DATE_OF_SERVICE || null,
      dateOfServiceFrom: fieldMappings.DATE_OF_SERVICE_FROM || null,
      dateOfServiceTo: fieldMappings.DATE_OF_SERVICE_TO || null,
      region: fieldMappings.REGION || null,
      locationId: fieldMappings.LOCATIONID || null,
      providerId: fieldMappings.PROVIDER_ID || null,
      reportType: fieldMappings.REPORT_TYPE || null,
      specialtyId: fieldMappings.SPECIALTY_ID || null,
      userId: fieldMappings.USERID || null,
      auditEvent: fieldMappings.AUDITEVENT || null,
      numberOfDays: fieldMappings.NUMBER_OF_DAYS || null,
      dateOfUploadFrom: fieldMappings.DATE_OF_UPLOAD_FROM || null,
      dateOfUploadTo: fieldMappings.DATE_OF_UPLOAD_TO || null,
      dateOfFaxFrom: fieldMappings.DATE_OF_FAX_FROM || null,
      dateOfFaxTo: fieldMappings.DATE_OF_FAX_TO || null,
      patientName: fieldMappings.PATIENT_NAME || null,
      mrn: fieldMappings.MRN || null,
      clientNotes: fieldMappings.CLIENT_NOTES || null,
      status: fieldMappings.STATUS || null,
      jobMonth: fieldMappings.JOB_MONTH || null,
      jobYear: fieldMappings.JOB_YEAR || null
    };
    console.log(reportFilter);
    //  await getReport(accountId||"", webDUser?.user.userId||"", selectedReport.key, reportFilter);
    try {
      const pdfBlob: Blob = await getReport(
        accountId || "",
        webDUser?.user.userId || "",
        selectedReport.key,
        reportFilter
      );

      const url = URL.createObjectURL(pdfBlob);

      window.open(url, "_blank", "noreferrer");
    } catch (error) {
      console.error("Error fetching report:", error);
    }
  };

  function generateYears(): { id: number; name: string }[] {
    const startYear = 2000;
    const currentYear = new Date().getFullYear();
    const years: { id: number; name: string }[] = [];

    for (let i = currentYear; i >= startYear; i--) {
      years.push({ id: i, name: i.toString() });
    }

    return years;
  }

  return (
    <>
      {!isParametersLoading ? (
        <>
          {shouldShowInputCard && (
            <Card style={{ marginBottom: "20px" }}>
              <Row gutter={[12, 12]}>
                {visibleControls.includes("REGION") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Region"
                      value={selectedRegionIds}
                      multiple={isMultiSelectEnabled("REGION")}
                      onChange={setSelectedRegionIds}
                      options={regions?.map(region => ({
                        id: region.regionId,
                        name: region.regionName
                      }))}
                    />
                  </Col>
                )}
                {visibleControls.includes("LOCATIONID") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Location"
                      value={selectedLocationIds}
                      multiple={isMultiSelectEnabled("LOCATIONID")}
                      onChange={setSelectedLocationIds}
                      options={locations?.map(location => ({
                        id: location.locationId,
                        name: location.locationName
                      }))}
                    />
                  </Col>
                )}
                {visibleControls.includes("PROVIDER_ID") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Provider"
                      value={selectedProviderIds}
                      multiple={isMultiSelectEnabled("PROVIDER_ID")}
                      onChange={setSelectedProviderIds}
                      options={providers?.map(provider => ({
                        id: provider.providerId,
                        name: provider.providerName
                      }))}
                    />
                  </Col>
                )}
                {visibleControls.includes("REPORT_TYPE") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Report Type"
                      value={selectedReportTypeIds}
                      multiple={isMultiSelectEnabled("REPORT_TYPE")}
                      onChange={setSelectedReportTypeIds}
                      options={documentTypes?.map(documentType => ({
                        id: documentType.documentTypeId,
                        name: documentType.documentTypeName
                      }))}
                    />
                  </Col>
                )}
                {visibleControls.includes("SPECIALTY_ID") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Speciality"
                      value={selectedSpecialityIds}
                      multiple={isMultiSelectEnabled("SPECIALTY_ID")}
                      onChange={setSelectedSpecialityIds}
                      options={specialities?.map(speciality => ({
                        id: speciality.specialityId,
                        name: speciality.description
                      }))}
                    />
                  </Col>
                )}
                {visibleControls.includes("USERID") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="User"
                      value={selectedUserIds}
                      multiple={isMultiSelectEnabled("USERID")}
                      onChange={setSelectedUserIds}
                      options={users?.map(user => ({
                        id: user.userId,
                        name: `${user.firstName}, ${user.lastName}`
                      }))}
                    />
                  </Col>
                )}
                {visibleControls.includes("STATUS") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Status"
                      value={selectedStatusIds}
                      multiple={isMultiSelectEnabled("STATUS")}
                      onChange={setSelectedStatusIds}
                      options={getStatusOptions()}
                    />
                  </Col>
                )}
                {/* {visibleControls.includes("AUDITEVENT") && (
            <Col xs={24} sm={12}>
              <InputField
                label="Audit Event"
                value={selectedAuditEventIds}
                onChange={setSelectedAuditEventIds}
                options={reportTypes}
              />
            </Col>
          )} */}
                {visibleControls.includes("NUMBER_OF_DAYS") && (
                  <Col xs={24} sm={12}>
                    <InputNumberField
                      label="Number of Days"
                      value={numberOfDays}
                      onChange={e => setNumberOfDays(parseInt(e.target.value, 10))}
                    />
                  </Col>
                )}
                {visibleControls.includes("PATIENT_NAME") && (
                  <Col xs={24} sm={12}>
                    <InputStringField
                      label="Patient Name"
                      value={patientName}
                      onChange={e => setPatientName(e.target.value)}
                    />
                  </Col>
                )}
                {visibleControls.includes("MRN") && (
                  <Col xs={24} sm={12}>
                    <InputStringField
                      label="MRN"
                      value={mrn}
                      onChange={e => setMrn(e.target.value)}
                    />
                  </Col>
                )}
                {visibleControls.includes("CLIENT_NOTES") && (
                  <Col xs={24} sm={24}>
                    <InputStringField
                      label="Client Notes"
                      value={clientNotes}
                      onChange={e => setClientNotes(e.target.value)}
                    />
                  </Col>
                )}
              </Row>
            </Card>
          )}

          {shouldShowDateCard && (
            <Card>
              <Row gutter={[12, 12]}>
                {visibleControls.includes("JOB_MONTH") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Job Month"
                      value={jobMonth}
                      onChange={setJobMonth}
                      options={months}
                    />
                  </Col>
                )}
                {visibleControls.includes("JOB_YEAR") && (
                  <Col xs={24} sm={12}>
                    <InputField
                      label="Job Year"
                      value={jobYear}
                      onChange={setJobYear}
                      options={generateYears().map(year => ({
                        id: year.id.toString(),
                        name: year.name
                      }))}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_DICTATION_FROM") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Dictation From"
                      onChange={setDateOfDictationFrom}
                      value={dateOfDictationFrom}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_DICTATION_TO") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Dictation To"
                      onChange={setDateOfDictationTo}
                      value={dateOfDictationTo}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_DICTATION") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Dictation"
                      onChange={setDateOfDictation}
                      value={dateOfDictation}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_SERVICE_FROM") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Service From"
                      onChange={setDateOfServiceFrom}
                      value={dateOfServiceFrom}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_SERVICE_TO") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Service To"
                      onChange={setDateOfServiceTo}
                      value={dateOfServiceTo}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_SERVICE") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Service"
                      onChange={setDateOfService}
                      value={dateOfService}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_UPLOAD_FROM") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Upload From"
                      onChange={setDateOfUploadFrom}
                      value={dateOfUploadFrom}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_UPLOAD_TO") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Date of Upload To"
                      onChange={setDateOfUploadTo}
                      value={dateOfUploadTo}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_FAX_FROM") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Fax Transmission From"
                      onChange={setDateOfFaxFrom}
                      value={dateOfFaxFrom}
                    />
                  </Col>
                )}
                {visibleControls.includes("DATE_OF_FAX_TO") && (
                  <Col xs={24} sm={12}>
                    <DatePickerField
                      label="Fax Transmission To"
                      onChange={setDateOfFaxTo}
                      value={dateOfFaxTo}
                    />
                  </Col>
                )}
              </Row>
            </Card>
          )}

          <div className="flex-container" style={{ marginTop: "2.25rem" }}>
            {webDUser?.hasPrivilege(WebDPrivilege.ManageReportAccess) && (
              <Button type="link" onClick={showModal} style={{ paddingLeft: "0" }}>
                Manage Report User Access
              </Button>
            )}
            <Button
              type="primary"
              className="default-button primary-btn"
              icon={<DocumentIcon className="default-icon" />}
              iconPosition={"start"}
              onClick={handleGenerateReport}>
              Generate Report
            </Button>
          </div>

          <ManageReportAccess isVisible={isModalVisible} handleClose={closeModal} />
        </>
      ) : (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Loading, please wait..." />
        </Flex>
      )}
    </>
  );
};

export default ReportFilter;
